/**
 * Payments pending — every unpaid invoice still owed to us, ALL TIME.
 * Deliberately independent of the report's date filter: an invoice raised
 * months ago is still pending today, and filtering it out is what made the
 * outstanding figures look wrong.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, ChevronRight, FileDown, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const BUCKETS = [
  { key: 'current', label: 'Not yet 30 days', cls: 'text-slate-700' },
  { key: 'd31_60', label: '31–60 days', cls: 'text-amber-700' },
  { key: 'd61_90', label: '61–90 days', cls: 'text-orange-700' },
  { key: 'd90_plus', label: 'Over 90 days', cls: 'text-red-700' },
];

const ageTone = (days) => {
  if (days > 90) return 'text-red-700';
  if (days > 60) return 'text-orange-700';
  if (days > 30) return 'text-amber-700';
  return 'text-slate-500';
};

const SOURCE_LABEL = {
  gst: 'GST books only',
  legacy: 'legacy books only',
  all: 'GST + legacy books',
};

const ReceivablesPanel = ({ source = 'all' }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [err, setErr] = useState('');

  // Fetched with the session cookie and opened from a blob — a plain link
  // would drop the auth header and render the login page as a PDF.
  const downloadStatement = async (c) => {
    setBusyId(c.customer_id); setErr('');
    try {
      const res = await axios.get(`${API}/reports/receivables/${c.customer_id}/statement`,
        { headers: authHeader(), params: { source }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pending_Payments_${(c.name || 'customer').replace(/\s+/g, '_')}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusyId(''); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: d } = await axios.get(`${API}/reports/receivables`,
        { headers: authHeader(), params: { source } });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, source]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="receivables-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <AlertCircle size={15} className="text-red-600" /> Payments pending
          <span className="text-xs font-normal text-slate-500">
            — every unpaid invoice to date in the {SOURCE_LABEL[source] || 'selected books'},
            ignores the date filter above
          </span>
        </h2>
        {data && (
          <div className="text-right">
            <div className="text-lg font-bold font-mono text-red-700" data-testid="receivables-total">
              ₹ {fmtINR(data.total_outstanding)}
            </div>
            <div className="text-[11px] text-slate-500" data-testid="receivables-meta">
              {data.invoice_count} invoice{data.invoice_count === 1 ? '' : 's'} · {data.customer_count} customer
              {data.customer_count === 1 ? '' : 's'} · as of {data.as_of}
            </div>
          </div>
        )}
      </div>

      {err && <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}

      {loading ? (
        <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
      ) : !data || data.customer_count === 0 ? (
        <div className="p-6 text-center text-emerald-700 text-sm" data-testid="receivables-empty">
          Nothing pending in the {SOURCE_LABEL[source] || 'selected books'} — every invoice is fully paid.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border-b border-slate-100">
            {BUCKETS.map((b) => (
              <div key={b.key} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`receivables-bucket-${b.key}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{b.label}</div>
                <div className={`text-sm font-bold font-mono ${b.cls}`}>₹ {fmtINR(data.aging[b.key])}</div>
              </div>
            ))}
          </div>

          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-100 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Customer</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Invoices</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Billed</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Received</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Pending</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Oldest</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Statement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.customers.map((c) => {
                const key = c.customer_id || c.name;
                const isOpen = !!open[key];
                return (
                  <React.Fragment key={key}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setOpen((p) => ({ ...p, [key]: !p[key] }))}
                      data-testid={`receivables-row-${c.name.replace(/[^a-z0-9]/gi, '-').slice(0, 25)}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <ChevronRight size={12} className={`inline mr-1 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        {c.name}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{c.invoices}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(c.billed)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtINR(c.paid)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-red-700">{fmtINR(c.balance)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${ageTone(c.oldest_days)}`}>{c.oldest_days}d</td>
                      <td className="px-3 py-2 text-right">
                        {c.customer_id ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadStatement(c); }}
                            disabled={busyId === c.customer_id}
                            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-white disabled:opacity-50"
                            title="Printable pending-payments statement (PDF)"
                            data-testid={`receivables-stmt-${c.customer_id}`}
                          >
                            {busyId === c.customer_id
                              ? <Loader2 size={11} className="animate-spin" />
                              : <FileDown size={11} />} PDF
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-400" title="This invoice has no linked customer record">—</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && c.rows.map((r) => (
                      <tr key={r.id} className="bg-slate-50/70" data-testid={`receivables-inv-${r.id}`}>
                        <td className="px-3 py-1.5 pl-8 text-slate-600">
                          {r.number}
                          <span className="ml-1.5 rounded bg-slate-200 px-1 py-0.5 text-[10px] uppercase text-slate-600">{r.stream}</span>
                          <span className="ml-1.5 text-slate-400">{r.date}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{r.status.replace('_', ' ')}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtINR(r.billed)}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmtINR(r.paid)}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-semibold text-red-700">{fmtINR(r.balance)}</td>
                        <td className={`px-3 py-1.5 text-right font-mono ${ageTone(r.age_days)}`}>{r.age_days}d</td>
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default ReceivablesPanel;
